import type { ReactNode } from "react";

import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { HubTableScrollRegion } from "@/components/layout/HubWorkspace";
import { Badge } from "@/components/ui/Badge";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { hubStickyTableHeadClassName } from "@/lib/hubLayoutClassNames";
import { EM_DASH } from "@/lib/typography";
import { cn } from "@/lib/utils";
import { InventoryDiscrepancyContextReferenceLink } from "@/pages/manufacturing/inventoryDiscrepancyContextReferenceLink";
import {
  formatSignedSkuContextQuantity,
  formatSkuContextDetailStatus,
  formatSkuContextKind,
  formatSkuContextRunningTotalDisplay,
  skuContextDetailStatusBadgeTone,
  type InventoryDiscrepancySkuContextRowWithRunningTotal,
} from "@/pages/manufacturing/inventoryDiscrepancySkuContext";
import {
  formatSkuContextBalanceQuantity,
  type InventoryDiscrepancySkuContextRowWithBalances,
} from "@/pages/manufacturing/skuContextBalances";

export type SkuInventoryTransactionContextTableProps = {
  rows: InventoryDiscrepancySkuContextRowWithRunningTotal[];
  loading?: boolean;
  loadingMessage?: ReactNode;
  error?: ReactNode;
  emptyMessage: ReactNode;
  showRunningTotal: boolean;
  /** Ledger, warehouse, floor, and receiving instead of the single running total. */
  showBalanceColumns?: boolean;
  showLocationColumn?: boolean;
  locationLabelForRow?: (row: InventoryDiscrepancySkuContextRowWithRunningTotal) => ReactNode;
  /** Compact panel (discrepancy detail) vs full viewport height (SKU tab). */
  layout?: "embedded" | "viewport";
  className?: string;
};

function BalanceColumnHeader(props: { label: string; hint: string }): JSX.Element {
  return (
    <span className="inline-flex items-center justify-end gap-1 normal-case tracking-normal">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{props.label}</span>
      <HoverHintWithGlyph align="end" hoverHint={props.hint} size="sm" />
    </span>
  );
}

function formatBalanceCell(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return String(value);
}

function BalanceCells(props: { row: InventoryDiscrepancySkuContextRowWithBalances }): JSX.Element {
  return (
    <>
      <td className="px-2 py-1.5 text-right tabular-nums">{formatBalanceCell(props.row.ledger_total)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{formatBalanceCell(props.row.warehouse_total)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{formatBalanceCell(props.row.floor_total)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{formatBalanceCell(props.row.receiving_total)}</td>
    </>
  );
}

function rowKey(row: InventoryDiscrepancySkuContextRowWithRunningTotal): string {
  return [
    row.kind,
    row.entity_id ?? "",
    row.entity_ref ?? "",
    row.mv_entity_id ?? "",
    row.occurred_at ?? "",
    row.reference ?? "",
    String(row.quantity ?? ""),
  ].join("|");
}

export function SkuInventoryTransactionContextTable(props: SkuInventoryTransactionContextTableProps): JSX.Element {
  const layout = props.layout ?? "embedded";
  const showLocation = props.showLocationColumn === true && props.locationLabelForRow != null;
  const showBalances = props.showBalanceColumns === true;
  const columnCount =
    5 + (showBalances ? 4 : props.showRunningTotal ? 1 : 0) + (showLocation ? 1 : 0);

  const table = (
    <table
      className={cn(
        "min-w-full border-collapse text-left",
        layout === "embedded" ? "text-xs" : "text-sm",
      )}
    >
      <thead className={hubStickyTableHeadClassName("slate")}>
        <tr>
          <th className="px-2 py-1.5">Date</th>
          <th className="px-2 py-1.5">Kind</th>
          <th className="px-2 py-1.5">Reference</th>
          <th className="px-2 py-1.5 text-right">Qty</th>
          {showBalances ? (
            <>
              <th className="px-2 py-1.5 text-right">
                <BalanceColumnHeader
                  label="Ledger"
                  hint="Megaventory transacted total. A hub receipt or a hub pick does not change it. A purchase invoice and a finished-good issue do."
                />
              </th>
              <th className="px-2 py-1.5 text-right">
                <BalanceColumnHeader
                  label="Warehouse"
                  hint="On the shelf. Ledger minus open floor picks minus transfers the hub has shipped that Megaventory has not transacted yet."
                />
              </th>
              <th className="px-2 py-1.5 text-right">
                <BalanceColumnHeader
                  label="Floor"
                  hint="Hub picks that a finished-good issue has not consumed. Still on the ledger, off the shelf."
                />
              </th>
              <th className="px-2 py-1.5 text-right">
                <BalanceColumnHeader
                  label="Receiving"
                  hint="Receipts that are not paired to a live purchase invoice. In the building, not on the ledger."
                />
              </th>
            </>
          ) : props.showRunningTotal ? (
            <th className="px-2 py-1.5 text-right">
              <span className="inline-flex items-center justify-end gap-1 normal-case tracking-normal">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Running total
                </span>
                <HoverHintWithGlyph
                  align="end"
                  hoverHint="On hand, then in house in parentheses. On hand is physical stock. Inbound receipts do not change it until a purchase invoice is created. Packing and work-order allocations stay in house, so they stay inside the parenthetical total."
                  size="sm"
                />
              </span>
            </th>
          ) : null}
          {showLocation ? <th className="px-2 py-1.5">Location</th> : null}
          <th className="px-2 py-1.5">Status</th>
        </tr>
      </thead>
      <tbody>
        {props.rows.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className="px-2 py-6 text-slate-600">
              {props.emptyMessage}
            </td>
          </tr>
        ) : (
          props.rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="border-t border-slate-100 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80"
            >
              <td className="px-2 py-1.5 whitespace-nowrap">
                {row.occurred_at?.slice(0, 10) ?? EM_DASH}
              </td>
              <td className="px-2 py-1.5">{formatSkuContextKind(row.kind)}</td>
              <td className="px-2 py-1.5">
                <InventoryDiscrepancyContextReferenceLink row={row} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {showBalances
                  ? formatSkuContextBalanceQuantity(row)
                  : formatSignedSkuContextQuantity(row)}
              </td>
              {showBalances ? (
                <BalanceCells row={row as InventoryDiscrepancySkuContextRowWithBalances} />
              ) : props.showRunningTotal ? (
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatSkuContextRunningTotalDisplay(row.on_hand_total, row.in_house_total)}
                </td>
              ) : null}
              {showLocation ? (
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {props.locationLabelForRow!(row)}
                </td>
              ) : null}
              <td className="px-2 py-1.5">
                {row.detail == null || row.detail.trim() === "" ? (
                  EM_DASH
                ) : (
                  <Badge tone={skuContextDetailStatusBadgeTone(row.detail, row.kind) ?? "neutral"}>
                    {formatSkuContextDetailStatus(row.detail)}
                  </Badge>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  if (props.loading) {
    return (
      <p className={cn("text-slate-600", layout === "embedded" ? "text-sm" : "px-4 py-6 text-sm")}>
        {props.loadingMessage ?? "Loading transaction context…"}
      </p>
    );
  }

  if (props.error != null) {
    return (
      <p className={cn("text-rose-700", layout === "embedded" ? "text-sm" : "px-4 py-6 text-sm")}>
        {props.error}
      </p>
    );
  }

  if (layout === "viewport") {
    return (
      <HubViewportCard chrome="standard" className={cn("overflow-hidden", props.className)}>
        <HubTableScrollRegion>{table}</HubTableScrollRegion>
      </HubViewportCard>
    );
  }

  return (
    <div className={cn("max-h-96 overflow-y-auto rounded-md border border-slate-200", props.className)}>
      {table}
    </div>
  );
}
